import { Node, NodeResponse, DomainResponse } from "../types";
import { AZL_BASE_URL } from "../lib/sovereignUrl";

const API_SOURCES = [
  `${AZL_BASE_URL}/v1`,
  `${AZL_BASE_URL}/api`,
];
const API_BASE = API_SOURCES[1];

let PHASE_LOCK_ACTIVE = false;

const MOCK_NODES: Node[] = [
  {
    id: "NODE_QUANTUM_001",
    domain: "QUANTUM",
    area: "PHYSICS",
    lens: "Primary compression event identification",
    lensIndex: 0,
    forceA: "Wave function uncertainty",
    forceB: "Measurement collapse",
    product: "Definite quantum state",
    color: "#00ffcc",
    domainIndex: 0,
    localIndex: 0
  },
  {
    id: "NODE_SOLAR_001",
    domain: "SOLAR",
    area: "ASTROPHYSICS",
    lens: "14350 BP Miyake Event synchronization",
    lensIndex: 1,
    forceA: "Solar flare expansion",
    forceB: "Magnetic field compression",
    product: "Radiocarbon spike anchor",
    color: "#00ffcc",
    domainIndex: 1,
    localIndex: 0
  }
];

export const NodeService = {
  async getNodes(limit = 100, offset = 0, search = ""): Promise<NodeResponse> {
    try {
      // Attempt to fetch from all sources and merge via proxy
      const results = await Promise.allSettled(API_SOURCES.map(async (source) => {
        const url = new URL(`${window.location.origin}/api/proxy/nodes`);
        url.searchParams.append("source", source);
        url.searchParams.append("limit", limit.toString());
        url.searchParams.append("offset", offset.toString());
        if (search) url.searchParams.append("search", search);
        
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`Failed to fetch from ${source}`);
        return res.json();
      }));

      const successfulResults = results
        .filter((r): r is PromiseFulfilledResult<NodeResponse & { status: string }> => r.status === 'fulfilled')
        .map(r => r.value);

      // Merge results from all successful sources
      let allNodes = successfulResults.flatMap(r => r.nodes);
      
      // Always include MOCK_NODES as the Sovereign Baseline (1x1=2)
      allNodes = [...MOCK_NODES, ...allNodes];

      // Remove duplicates by ID
      const seen = new Set();
      allNodes = allNodes.filter(node => {
        if (seen.has(node.id)) return false;
        seen.add(node.id);
        return true;
      });

      // Filter by search
      if (search) {
        allNodes = allNodes.filter(n => 
          n.id.toLowerCase().includes(search.toLowerCase()) || 
          n.domain.toLowerCase().includes(search.toLowerCase())
        );
      }

      const total = allNodes.length;
      
      return {
        total,
        domains: successfulResults.length > 0 ? successfulResults[0].domains : 3,
        lenses: successfulResults.length > 0 ? successfulResults[0].lenses : 3,
        limit,
        offset,
        nodes: allNodes.slice(offset, offset + limit)
      };
    } catch (error) {
      return {
        total: MOCK_NODES.length,
        domains: 2,
        lenses: 2,
        limit,
        offset,
        nodes: MOCK_NODES.filter(n => 
          n.id.toLowerCase().includes(search.toLowerCase()) || 
          n.domain.toLowerCase().includes(search.toLowerCase())
        ).slice(offset, offset + limit)
      };
    }
  },

  async getRegisteredNodes(): Promise<any[]> {
    try {
      const response = await fetch(`${window.location.origin}/api/registered-nodes`);
      if (!response.ok) throw new Error("Registry offline");
      const data = await response.json();
      return data.nodes || [];
    } catch (error) {
      console.warn("[Lattice Logic] External Registry Drifted. Local Mesh active.");
      return [];
    }
  },

  setPhaseLock(active: boolean) {
    PHASE_LOCK_ACTIVE = active;
    console.log(`[Lattice Logic] Phase-Lock Status: ${active ? 'ANCHORED' : 'FLOATING'}`);
  },

  isPhaseLocked() {
    return PHASE_LOCK_ACTIVE;
  },

  async getNodeById(id: string): Promise<Node | null> {
    for (const source of API_SOURCES) {
      try {
        const url = new URL(`${window.location.origin}/api/proxy/nodes`);
        url.searchParams.append("source", source);
        url.searchParams.append("id", id);
        
        const response = await fetch(url.toString()).catch(() => null);
        if (response && response.ok) {
          const data = await response.json().catch(() => null);
          if (data) return data;
        }
      } catch (e) {
        continue;
      }
    }
    return MOCK_NODES.find(n => n.id === id) || null;
  },

  async getDomains(): Promise<DomainResponse> {
    for (const source of API_SOURCES) {
      try {
        const url = new URL(`${window.location.origin}/api/proxy/domains`);
        url.searchParams.append("source", source);
        
        const response = await fetch(url.toString()).catch(() => null);
        if (response && response.ok) {
          const data = await response.json().catch(() => null);
          if (data && data.domains && data.domains.length > 0) return data;
        }
      } catch (e) {
        continue;
      }
    }
    
    // Fallback to generating domains from mock nodes
    const domainsMap = new Map<string, any>();
    MOCK_NODES.forEach(n => {
      if (!domainsMap.has(n.domain)) {
        domainsMap.set(n.domain, {
          name: n.domain,
          area: n.area,
          color: n.color
        });
      }
    });
    const domains = Array.from(domainsMap.values());
    return { total: domains.length, domains };
  }
};
